import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import { 
  Plus, Save, X, Search, MapPin, Lock, Check, ChevronDown, ChevronRight,
  Upload, Film, Loader2, Eye, Maximize2, RotateCcw, Globe, Clock, FileText, 
  Sparkles, Grid3X3, Calendar, Ticket, Users, Music, Wine, Building, Car,
  Copy, GripVertical, Image, Hotel, HelpCircle, Zap, Star, Coffee, Utensils,
  PartyPopper, Map, Link, Trash2, ExternalLink, Settings, LayoutGrid,
  Monitor, Smartphone, Link2, AlertCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ CONSTANTS ============
const STATUS_CONFIG = {
  live: { 
    label: "LIVE", 
    color: "#C9F400", 
    bg: "rgba(201, 244, 0, 0.08)", 
    border: "rgba(201, 244, 0, 0.15)",
    glow: "0 0 20px rgba(201, 244, 0, 0.15)"
  },
  coming_soon: { 
    label: "COMING SOON", 
    color: "#F59E0B", 
    bg: "rgba(245, 158, 11, 0.08)", 
    border: "rgba(245, 158, 11, 0.15)",
    glow: "0 0 20px rgba(245, 158, 11, 0.15)"
  },
  draft: { 
    label: "DRAFT", 
    color: "#6B7280", 
    bg: "rgba(107, 114, 128, 0.08)", 
    border: "rgba(107, 114, 128, 0.15)",
    glow: "none"
  },
};

const HERO_STYLES = [
  { id: "skull", label: "Sugar Skull", icon: "☠️" },
  { id: "video", label: "Video", icon: "🎬" },
  { id: "image", label: "Image", icon: "🖼️" },
  { id: "none", label: "None", icon: "⬛" },
];

const FEATURE_ICONS = [
  { id: "ticket", icon: Ticket, label: "Ticket" },
  { id: "users", icon: Users, label: "Group" },
  { id: "music", icon: Music, label: "Music" },
  { id: "wine", icon: Wine, label: "Drinks" },
  { id: "utensils", icon: Utensils, label: "Food" },
  { id: "star", icon: Star, label: "VIP" },
  { id: "zap", icon: Zap, label: "Energy" },
  { id: "party", icon: PartyPopper, label: "Party" },
];

// ============ UTILITY COMPONENTS ============

const Toast = ({ message, type = "success", onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    className={`fixed bottom-6 right-6 z-[100] px-5 py-3.5 rounded-2xl flex items-center gap-3 shadow-2xl backdrop-blur-xl border ${
      type === "success" ? "bg-[#0d1f0d]/90 border-[#C9F400]/20" : "bg-[#1f0d0d]/90 border-red-500/20"
    }`}
  >
    <div className={`w-2 h-2 rounded-full ${type === "success" ? "bg-[#C9F400]" : "bg-red-400"}`} />
    <span className="text-white/90 text-sm font-medium">{message}</span>
    <button onClick={onClose} className="text-white/40 hover:text-white/70 ml-2 transition-colors">
      <X className="w-4 h-4" />
    </button>
  </motion.div>
);

const StatusBadge = ({ status, size = "default", glow = false }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const isSmall = size === "small";
  
  return (
    <div 
      className={`inline-flex items-center gap-1.5 ${isSmall ? "px-2 py-0.5" : "px-3 py-1.5"} rounded-full transition-all duration-300`}
      style={{ 
        background: config.bg, 
        border: `1px solid ${config.border}`,
        boxShadow: glow ? config.glow : "none"
      }}
    >
      <motion.div 
        className={`${isSmall ? "w-1.5 h-1.5" : "w-2 h-2"} rounded-full`} 
        style={{ background: config.color }}
        animate={status === "live" ? { scale: [1, 1.2, 1], opacity: [1, 0.7, 1] } : {}}
        transition={{ duration: 2, repeat: Infinity }}
      />
      <span 
        className={`${isSmall ? "text-[9px]" : "text-[10px]"} font-bold uppercase tracking-wider`}
        style={{ color: config.color }}
      >
        {config.label}
      </span>
    </div>
  );
};

// Collapsible Section
const Section = ({ title, icon: Icon, children, defaultOpen = false, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-white/[0.04] last:border-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 px-1 group"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-4 h-4 text-white/30" />}
          <span className="text-white/80 text-sm font-medium">{title}</span>
          {badge && (
            <span className="text-[10px] text-white/30 bg-white/[0.04] px-2 py-0.5 rounded-full">{badge}</span>
          )}
        </div>
        <motion.div animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors" />
        </motion.div>
      </button>
      
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-5 px-1 space-y-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Premium Input
const Input = ({ label, value, onChange, placeholder, type = "text", required, suffix, className = "", ...props }) => (
  <div className={`space-y-1.5 ${className}`}>
    {label && (
      <label className="text-[10px] text-white/40 uppercase tracking-wider font-semibold flex items-center gap-1">
        {label} {required && <span className="text-[#C9F400]">*</span>}
      </label>
    )}
    <div className="relative">
      <input
        type={type}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-white/[0.03] hover:bg-white/[0.05] border border-white/[0.06] hover:border-white/[0.1] rounded-xl px-4 py-3 text-white/90 text-sm placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/40 focus:bg-white/[0.05] transition-all duration-200"
        {...props}
      />
      {suffix && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 text-xs">{suffix}</span>
      )}
    </div>
  </div>
);

// Premium Textarea
const Textarea = ({ label, value, onChange, placeholder, rows = 3 }) => (
  <div className="space-y-1.5">
    {label && <label className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">{label}</label>}
    <textarea
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full bg-white/[0.03] hover:bg-white/[0.05] border border-white/[0.06] hover:border-white/[0.1] rounded-xl px-4 py-3 text-white/90 text-sm placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/40 transition-all duration-200 resize-none"
    />
  </div>
);

// Toggle Switch
const Toggle = ({ label, value, onChange, description }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <span className="text-white/80 text-sm font-medium">{label}</span>
      {description && <p className="text-white/30 text-xs mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-11 h-6 rounded-full transition-all duration-300 ${value ? "bg-[#C9F400]/30" : "bg-white/10"}`}
    >
      <motion.div
        className={`w-5 h-5 rounded-full shadow-lg ${value ? "bg-[#C9F400]" : "bg-white/50"}`}
        animate={{ x: value ? 22 : 2 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      />
    </button>
  </div>
);

// File Uploader
const FileUploader = ({ value, onChange, label = "File", accept = "video/*,image/*" }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const progressInterval = setInterval(() => setProgress(p => Math.min(p + 15, 90)), 150);
    
    try {
      const res = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: formData });
      clearInterval(progressInterval);
      if (res.ok) {
        const data = await res.json();
        setProgress(100);
        onChange(`${API_URL}${data.url}`);
      }
    } catch (err) { console.error(err); }
    finally { setUploading(false); setProgress(0); }
  };

  return (
    <div className="space-y-2">
      <label className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">{label}</label>
      {value && (
        <div className="flex items-center gap-2 p-3 bg-white/[0.03] rounded-xl border border-white/[0.06]">
          <Film className="w-4 h-4 text-[#C9F400]" />
          <span className="text-white/60 text-xs truncate flex-1">{value.split('/').pop()}</span>
          <button onClick={() => onChange('')} className="text-white/30 hover:text-red-400 transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        className={`border border-dashed border-white/10 hover:border-white/20 rounded-xl p-5 cursor-pointer transition-all ${uploading ? "opacity-50 pointer-events-none" : ""}`}
      >
        <input ref={inputRef} type="file" accept={accept} onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-5 h-5 text-[#C9F400] animate-spin" />
            <div className="w-full max-w-[120px] bg-white/10 rounded-full h-1">
              <div className="bg-[#C9F400] h-1 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-white/30">
            <Upload className="w-5 h-5" />
            <span className="text-xs font-medium">Drop file or click</span>
          </div>
        )}
      </div>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste URL..."
        className="w-full bg-white/[0.02] border border-white/[0.04] rounded-lg px-3 py-2 text-white/70 text-xs placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/30"
      />
    </div>
  );
};

// Venue Card Builder
const VenueBuilder = ({ venues = [], setVenues }) => {
  const addVenue = () => setVenues([...venues, { name: "", tagline: "", theme: "", address: "", order: venues.length }]);
  const updateVenue = (i, field, val) => {
    const updated = [...venues];
    updated[i] = { ...updated[i], [field]: val };
    setVenues(updated);
  };
  const removeVenue = (i) => setVenues(venues.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      {venues.map((venue, i) => (
        <motion.div 
          key={i} 
          layout
          className="p-4 bg-white/[0.02] rounded-xl border border-white/[0.06] space-y-3 group"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 text-white/40">
              <GripVertical className="w-4 h-4 cursor-grab" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Venue {i + 1}</span>
            </div>
            <button onClick={() => removeVenue(i)} className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition-all">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Venue Name" value={venue.name} onChange={(e) => updateVenue(i, "name", e.target.value)} />
            <Input placeholder="Theme / Vibe" value={venue.theme} onChange={(e) => updateVenue(i, "theme", e.target.value)} />
          </div>
          <Input placeholder="Short Tagline" value={venue.tagline} onChange={(e) => updateVenue(i, "tagline", e.target.value)} />
          <Input placeholder="Address (optional)" value={venue.address} onChange={(e) => updateVenue(i, "address", e.target.value)} />
        </motion.div>
      ))}
      <button onClick={addVenue} className="w-full py-3 text-white/40 hover:text-[#C9F400] text-xs uppercase tracking-wider font-semibold hover:bg-white/[0.02] rounded-xl border border-dashed border-white/[0.08] hover:border-[#C9F400]/30 transition-all flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> Add Venue
      </button>
    </div>
  );
};

// Feature List Builder
const FeatureBuilder = ({ features = [], setFeatures }) => {
  const addFeature = () => setFeatures([...features, { text: "", icon: "ticket" }]);
  const updateFeature = (i, field, val) => {
    const updated = [...features];
    updated[i] = { ...updated[i], [field]: val };
    setFeatures(updated);
  };
  const removeFeature = (i) => setFeatures(features.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {features.map((feat, i) => (
        <div key={i} className="flex gap-2 group">
          <select
            value={feat.icon || "ticket"}
            onChange={(e) => updateFeature(i, "icon", e.target.value)}
            className="w-20 bg-white/[0.03] border border-white/[0.06] rounded-lg px-2 py-2.5 text-white/70 text-xs focus:outline-none focus:border-[#C9F400]/30"
          >
            {FEATURE_ICONS.map((ic) => (
              <option key={ic.id} value={ic.id}>{ic.label}</option>
            ))}
          </select>
          <input
            value={feat.text || (typeof feat === 'string' ? feat : '')}
            onChange={(e) => updateFeature(i, "text", e.target.value)}
            placeholder="Feature description"
            className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white/90 text-sm placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/30"
          />
          <button onClick={() => removeFeature(i)} className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 p-2 transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button onClick={addFeature} className="w-full py-2.5 text-white/40 hover:text-[#C9F400] text-xs uppercase tracking-wider font-semibold hover:bg-white/[0.02] rounded-lg border border-dashed border-white/[0.08] hover:border-[#C9F400]/30 transition-all flex items-center justify-center gap-2">
        <Plus className="w-3 h-3" /> Add Feature
      </button>
    </div>
  );
};

// Hotel Builder
const HotelBuilder = ({ hotels = [], setHotels }) => {
  const addHotel = () => setHotels([...hotels, { name: "", distance: "", bookingUrl: "" }]);
  const updateHotel = (i, field, val) => {
    const updated = [...hotels];
    updated[i] = { ...updated[i], [field]: val };
    setHotels(updated);
  };
  const removeHotel = (i) => setHotels(hotels.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {hotels.map((hotel, i) => (
        <div key={i} className="flex gap-2 group items-start">
          <div className="flex-1 grid grid-cols-3 gap-2">
            <input value={hotel.name} onChange={(e) => updateHotel(i, "name", e.target.value)} placeholder="Hotel Name"
              className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white/90 text-sm placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/30" />
            <input value={hotel.distance || hotel.note} onChange={(e) => updateHotel(i, "distance", e.target.value)} placeholder="Distance"
              className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white/90 text-sm placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/30" />
            <input value={hotel.bookingUrl} onChange={(e) => updateHotel(i, "bookingUrl", e.target.value)} placeholder="Booking URL"
              className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white/90 text-sm placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/30" />
          </div>
          <button onClick={() => removeHotel(i)} className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 p-2 transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button onClick={addHotel} className="w-full py-2.5 text-white/40 hover:text-[#C9F400] text-xs uppercase tracking-wider font-semibold hover:bg-white/[0.02] rounded-lg border border-dashed border-white/[0.08] hover:border-[#C9F400]/30 transition-all flex items-center justify-center gap-2">
        <Plus className="w-3 h-3" /> Add Hotel
      </button>
    </div>
  );
};

// FAQ Builder
const FAQBuilder = ({ faq = [], setFaq }) => {
  const addFaq = () => setFaq([...faq, { question: "", answer: "" }]);
  const updateFaq = (i, field, val) => {
    const updated = [...faq];
    updated[i] = { ...updated[i], [field]: val };
    setFaq(updated);
  };
  const removeFaq = (i) => setFaq(faq.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      {faq.map((item, i) => (
        <div key={i} className="p-3 bg-white/[0.02] rounded-xl border border-white/[0.06] space-y-2 group">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-white/30 font-bold uppercase tracking-wider">Q{i + 1}</span>
            <button onClick={() => removeFaq(i)} className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition-all">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <input value={item.question} onChange={(e) => updateFaq(i, "question", e.target.value)} placeholder="Question"
            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white/90 text-sm placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/30" />
          <textarea value={item.answer} onChange={(e) => updateFaq(i, "answer", e.target.value)} placeholder="Answer" rows={2}
            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white/90 text-sm placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/30 resize-none" />
        </div>
      ))}
      <button onClick={addFaq} className="w-full py-2.5 text-white/40 hover:text-[#C9F400] text-xs uppercase tracking-wider font-semibold hover:bg-white/[0.02] rounded-lg border border-dashed border-white/[0.08] hover:border-[#C9F400]/30 transition-all flex items-center justify-center gap-2">
        <Plus className="w-3 h-3" /> Add FAQ
      </button>
    </div>
  );
};

// ============ PREVIEW COMPONENTS ============

const PreviewCityCard = ({ data }) => {
  const isLive = data.status === "live";
  return (
    <div className="p-8 flex items-center justify-center">
      <div className="w-full max-w-[300px]">
        <motion.div 
          className={`relative h-[120px] flex items-center justify-center rounded-2xl transition-all overflow-hidden ${
            isLive 
              ? "bg-gradient-to-br from-[#C9F400]/15 to-[#C9F400]/5" 
              : "bg-gradient-to-br from-white/[0.06] to-white/[0.02]"
          }`}
          style={{ 
            border: `1px solid ${isLive ? "rgba(201,244,0,0.3)" : "rgba(255,255,255,0.08)"}`,
            boxShadow: isLive ? "0 8px 32px rgba(201,244,0,0.1)" : "0 8px 32px rgba(0,0,0,0.3)"
          }}
          whileHover={{ y: -4, boxShadow: isLive ? "0 16px 48px rgba(201,244,0,0.15)" : "0 16px 48px rgba(0,0,0,0.4)" }}
        >
          {isLive && (
            <span className="absolute top-3 right-3 px-2.5 py-1 text-[9px] font-black bg-[#C9F400] text-black rounded-md uppercase tracking-wider">
              On Sale
            </span>
          )}
          <span className={`font-black text-3xl uppercase tracking-wide ${isLive ? "text-[#C9F400]" : "text-white/80"}`}>
            {data.cityName || "CITY"}
          </span>
        </motion.div>
        <p className="text-center text-white/40 text-sm mt-3 font-medium">{data.shortLocationLine || "Downtown"}</p>
      </div>
    </div>
  );
};

const PreviewLivePage = ({ data }) => (
  <div className="p-4 space-y-4">
    {/* Hero */}
    <div className="relative h-[220px] bg-gradient-to-b from-[#0F3B2E]/60 to-[#0a0a0c] rounded-2xl overflow-hidden flex items-center justify-center">
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.08]">
        <span className="text-[140px]">☠️</span>
      </div>
      <div className="relative z-10 text-center">
        <p className="text-white/50 text-sm font-semibold tracking-[0.2em]">CINCO DE CRAWL</p>
        <h1 className="text-5xl font-black text-[#C24E2C] mt-2">{data.cityName || "CITY"}</h1>
        <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto my-4" />
        <p className="text-[#D4AF37] font-bold">{data.eventDate || "MAY 5, 2026"}</p>
        <p className="text-white/40 text-sm mt-1">{data.shortLocationLine}</p>
        <button className="mt-5 px-8 py-3 bg-[#C9F400] hover:bg-[#d4f520] text-black font-bold text-sm rounded-xl transition-colors">
          GET TICKETS
        </button>
      </div>
    </div>
    
    {/* What You Get */}
    {data.whatYouGet?.length > 0 && (
      <div className="bg-white/[0.03] rounded-2xl p-5 border border-white/[0.06]">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-4">What You Get</h3>
        <div className="grid grid-cols-2 gap-3">
          {(data.whatYouGet || []).slice(0, 6).map((item, i) => {
            const text = typeof item === 'string' ? item : item.text;
            return (
              <div key={i} className="flex items-center gap-2.5 text-sm text-white/70">
                <Check className="w-4 h-4 text-[#C9F400] flex-shrink-0" /> 
                <span>{text}</span>
              </div>
            );
          })}
        </div>
      </div>
    )}
    
    {/* Venues */}
    {data.featuredVenues?.length > 0 && (
      <div className="bg-white/[0.03] rounded-2xl p-5 border border-white/[0.06]">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-4">Featured Venues</h3>
        <div className="space-y-3">
          {(data.featuredVenues || []).slice(0, 4).map((venue, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
              <div>
                <span className="text-white/80 font-medium text-sm">{venue.name}</span>
                {venue.tagline && <p className="text-white/30 text-xs mt-0.5">{venue.tagline}</p>}
              </div>
              <span className="text-white/30 text-xs bg-white/[0.04] px-2 py-1 rounded">{venue.theme || venue.vibe}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const PreviewComingSoon = ({ data }) => (
  <div className="p-4">
    <div className="h-[320px] bg-gradient-to-b from-[#0F3B2E]/40 to-[#0a0a0c] rounded-2xl flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.05]">
        <span className="text-[160px]">☠️</span>
      </div>
      <div className="relative z-10 text-center">
        <p className="text-white/40 text-sm tracking-[0.2em] font-semibold mb-3">CINCO DE CRAWL</p>
        <h1 className="text-5xl font-black text-[#C24E2C]">{data.cityName || "CITY"}</h1>
        <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto my-5" />
        <p className="text-[#D4AF37] font-bold text-lg">{data.eventDate || "MAY 5, 2026"}</p>
        <p className="text-white/40 mt-3 text-sm">{data.comingSoonSubline || "Tickets dropping soon."}</p>
        <button className="mt-8 px-8 py-3 bg-[#C9F400] hover:bg-[#d4f520] text-black font-bold text-sm rounded-xl transition-colors">
          {data.cityPageCtaText || "GET PRIORITY ACCESS"}
        </button>
      </div>
    </div>
  </div>
);

// Fullscreen Preview Modal
const FullscreenPreview = ({ data, mode, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[200] bg-[#0a0a0c] overflow-auto"
  >
    <div className="sticky top-0 z-10 bg-[#0a0a0c]/90 backdrop-blur-xl border-b border-white/[0.06] px-8 py-5 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
          <Eye className="w-5 h-5 text-white/40" />
        </div>
        <div>
          <h2 className="text-white font-semibold text-lg">{data.cityName || "City"}</h2>
          <p className="text-white/30 text-xs">Full Preview • {mode === "live" ? "Live Page" : mode === "coming" ? "Coming Soon" : "City Card"}</p>
        </div>
      </div>
      <button onClick={onClose} className="p-2.5 hover:bg-white/[0.06] rounded-xl transition-colors">
        <X className="w-5 h-5 text-white/50" />
      </button>
    </div>
    <div className="max-w-5xl mx-auto py-8 px-4">
      {mode === "card" && <PreviewCityCard data={data} />}
      {mode === "live" && <PreviewLivePage data={data} />}
      {mode === "coming" && <PreviewComingSoon data={data} />}
    </div>
  </motion.div>
);

// ============ CONSUMER PREVIEW SYSTEM ============

// Full Live Page Template (Consumer View)
const ConsumerLiveTemplate = ({ data, isMobile }) => {
  const sugarSkullUrl = "https://static.prod-images.emergentagent.com/jobs/8bbf1b5c-1903-4355-95c6-d589de2e5570/images/69c4b957b2b796f7412a1d8087bf6fdacf65c7c9a6f79a7fa98e659e377faeaa.png";
  
  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white">
      {/* Hero Section */}
      <section className={`relative ${isMobile ? "min-h-[90vh]" : "min-h-screen"} flex flex-col items-center justify-center px-4 pt-20 pb-16 overflow-hidden`}>
        {/* Sugar Skull Background */}
        {data.heroBackgroundStyle === "skull" && (
          <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none">
            <div className="relative w-full h-full flex items-center justify-center"
              style={{ maskImage: 'radial-gradient(ellipse 80% 90% at 50% 50%, black 40%, transparent 80%)', WebkitMaskImage: 'radial-gradient(ellipse 80% 90% at 50% 50%, black 40%, transparent 80%)' }}>
              <img src={sugarSkullUrl} alt="" className="absolute skull-watermark"
                style={{ height: '140%', width: 'auto', maxWidth: 'none', objectFit: 'contain', opacity: isMobile ? 0.12 : 0.28, filter: 'blur(2px) saturate(0.8)', transform: 'translateY(-5%)' }} />
            </div>
          </div>
        )}
        
        {/* Overlays */}
        <div className="absolute inset-0 z-[2] pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 15% 20%, rgba(15,59,46,0.35) 0%, transparent 50%)' }} />
        <div className="absolute inset-0 z-[3] pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent 0%, transparent 50%, rgba(11,11,15,0.5) 80%, rgba(11,11,15,0.8) 100%)' }} />
        
        {/* Content */}
        <div className="text-center max-w-4xl mx-auto relative z-10">
          <p className={`text-white/60 ${isMobile ? "text-sm" : "text-lg"} font-semibold tracking-[0.3em] mb-4`}>CINCO DE CRAWL</p>
          <h1 className={`font-black ${isMobile ? "text-5xl" : "text-7xl"} text-[#C24E2C] uppercase`}>{data.cityName || "CITY"}</h1>
          <div className={`${isMobile ? "w-12" : "w-20"} h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto my-6`} />
          <p className={`text-[#D4AF37] font-bold ${isMobile ? "text-lg" : "text-xl"}`}>{data.eventDate || "MAY 5, 2026"}</p>
          <p className={`text-white/50 ${isMobile ? "text-sm" : "text-base"} mt-2`}>{data.shortLocationLine || "Downtown"}</p>
          
          <div className={`flex ${isMobile ? "flex-col" : "flex-row"} items-center justify-center gap-4 mt-8`}>
            <button className={`${isMobile ? "w-full" : ""} px-10 py-4 bg-[#C9F400] hover:bg-[#d4f520] text-black font-bold ${isMobile ? "text-sm" : "text-base"} rounded-xl transition-all shadow-lg shadow-[#C9F400]/20`}>
              GET TICKETS
            </button>
            <button className={`${isMobile ? "w-full" : ""} px-8 py-4 border border-[#D4AF37]/40 text-[#D4AF37] font-semibold ${isMobile ? "text-sm" : "text-base"} rounded-xl hover:bg-[#D4AF37]/10 transition-all`}>
              VIP ACCESS
            </button>
          </div>
        </div>
      </section>
      
      {/* What You Get Section */}
      {data.whatYouGet?.length > 0 && (
        <section className={`${isMobile ? "px-4 py-12" : "px-8 py-20"} bg-[#0F3B2E]/30`}>
          <div className="max-w-4xl mx-auto">
            <h2 className={`text-center font-bold ${isMobile ? "text-2xl" : "text-3xl"} text-white mb-8 uppercase tracking-wider`}>What You Get</h2>
            <div className={`grid ${isMobile ? "grid-cols-1 gap-4" : "grid-cols-2 gap-6"}`}>
              {(data.whatYouGet || []).map((item, i) => {
                const text = typeof item === 'string' ? item : item.text;
                return (
                  <div key={i} className="flex items-center gap-4 p-4 bg-white/[0.04] rounded-xl border border-white/[0.06]">
                    <div className="w-10 h-10 rounded-lg bg-[#C9F400]/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-[#C9F400]" />
                    </div>
                    <span className={`text-white/80 ${isMobile ? "text-sm" : "text-base"} font-medium`}>{text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
      
      {/* Venues Section */}
      {data.featuredVenues?.length > 0 && (
        <section className={`${isMobile ? "px-4 py-12" : "px-8 py-20"}`}>
          <div className="max-w-4xl mx-auto">
            <h2 className={`text-center font-bold ${isMobile ? "text-2xl" : "text-3xl"} text-white mb-8 uppercase tracking-wider`}>Featured Venues</h2>
            <div className={`grid ${isMobile ? "grid-cols-1 gap-4" : "grid-cols-2 gap-6"}`}>
              {(data.featuredVenues || []).map((venue, i) => (
                <div key={i} className="p-5 bg-white/[0.03] rounded-2xl border border-white/[0.06] hover:border-white/[0.12] transition-all">
                  <h3 className={`font-bold ${isMobile ? "text-lg" : "text-xl"} text-white`}>{venue.name}</h3>
                  {venue.tagline && <p className="text-white/50 text-sm mt-1">{venue.tagline}</p>}
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs bg-[#C24E2C]/20 text-[#C24E2C] px-3 py-1 rounded-full font-medium">{venue.theme || venue.vibe}</span>
                  </div>
                  {venue.address && <p className="text-white/30 text-xs mt-3 flex items-center gap-2"><MapPin className="w-3 h-3" /> {venue.address}</p>}
                </div>
              ))}
            </div>
            {data.moreVenuesComing && (
              <p className="text-center text-white/40 text-sm mt-6 italic">+ More venues to be announced</p>
            )}
          </div>
        </section>
      )}
      
      {/* Hotels Section */}
      {data.hotelsNearby?.length > 0 && (
        <section className={`${isMobile ? "px-4 py-12" : "px-8 py-20"} bg-white/[0.02]`}>
          <div className="max-w-4xl mx-auto">
            <h2 className={`text-center font-bold ${isMobile ? "text-2xl" : "text-3xl"} text-white mb-8 uppercase tracking-wider`}>Stay Nearby</h2>
            <div className={`grid ${isMobile ? "grid-cols-1 gap-3" : "grid-cols-3 gap-4"}`}>
              {(data.hotelsNearby || []).map((hotel, i) => (
                <div key={i} className="p-4 bg-white/[0.03] rounded-xl border border-white/[0.06] flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-white text-sm">{hotel.name}</h4>
                    <p className="text-white/40 text-xs mt-1">{hotel.distance || hotel.note}</p>
                  </div>
                  {hotel.bookingUrl && (
                    <ExternalLink className="w-4 h-4 text-white/30" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* FAQ Section */}
      {data.faq?.length > 0 && (
        <section className={`${isMobile ? "px-4 py-12" : "px-8 py-20"}`}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`text-center font-bold ${isMobile ? "text-2xl" : "text-3xl"} text-white mb-8 uppercase tracking-wider`}>FAQ</h2>
            <div className="space-y-4">
              {(data.faq || []).map((item, i) => (
                <div key={i} className="p-5 bg-white/[0.03] rounded-xl border border-white/[0.06]">
                  <h4 className={`font-semibold text-white ${isMobile ? "text-sm" : "text-base"}`}>{item.question}</h4>
                  <p className={`text-white/60 ${isMobile ? "text-xs" : "text-sm"} mt-2 leading-relaxed`}>{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Footer CTA */}
      <section className={`${isMobile ? "px-4 py-16" : "px-8 py-24"} bg-gradient-to-b from-[#0F3B2E]/30 to-[#0B0B0F] text-center`}>
        <h2 className={`font-black ${isMobile ? "text-3xl" : "text-4xl"} text-white mb-4`}>Ready to Crawl?</h2>
        <p className={`text-white/50 ${isMobile ? "text-sm" : "text-base"} mb-8`}>{data.eventDate || "May 5, 2026"} • {data.shortLocationLine || "Downtown"}</p>
        <button className={`px-12 py-4 bg-[#C9F400] hover:bg-[#d4f520] text-black font-bold ${isMobile ? "text-sm" : "text-base"} rounded-xl transition-all shadow-lg shadow-[#C9F400]/20`}>
          GET TICKETS NOW
        </button>
      </section>
    </div>
  );
};

// Full Coming Soon Template (Consumer View)
const ConsumerComingSoonTemplate = ({ data, isMobile }) => (
  <div className="min-h-screen bg-[#0B0B0F] text-white flex flex-col items-center justify-center px-4 relative overflow-hidden">
    {/* Background decoration */}
    <div className="absolute inset-0 flex items-center justify-center opacity-[0.04] pointer-events-none">
      <span className={`${isMobile ? "text-[200px]" : "text-[400px]"}`}>☠️</span>
    </div>
    
    {/* Gradient overlays */}
    <div className="absolute inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(15,59,46,0.3) 0%, transparent 60%)' }} />
    
    <div className="relative z-10 text-center max-w-2xl">
      <p className={`text-white/40 ${isMobile ? "text-sm" : "text-base"} font-semibold tracking-[0.3em] mb-6`}>CINCO DE CRAWL</p>
      
      <h1 className={`font-black ${isMobile ? "text-5xl" : "text-8xl"} text-[#C24E2C] uppercase leading-none`}>
        {data.cityName || "CITY"}
      </h1>
      
      <div className={`${isMobile ? "w-16" : "w-24"} h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto my-8`} />
      
      <p className={`text-[#D4AF37] font-bold ${isMobile ? "text-xl" : "text-2xl"} mb-4`}>{data.eventDate || "MAY 5, 2026"}</p>
      
      <h2 className={`font-black ${isMobile ? "text-2xl" : "text-4xl"} text-white/90 mb-4 uppercase tracking-wide`}>
        {data.comingSoonHeadline || "ARE YOU READY?"}
      </h2>
      
      <p className={`text-white/50 ${isMobile ? "text-base" : "text-lg"} mb-10`}>
        {data.comingSoonSubline || "Tickets dropping soon."}
      </p>
      
      {data.emailCaptureEnabled && (
        <div className={`${isMobile ? "w-full" : "max-w-md mx-auto"}`}>
          <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-3`}>
            <input 
              type="email" 
              placeholder="Enter your email" 
              className={`flex-1 px-5 py-4 bg-white/[0.05] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9F400]/40 ${isMobile ? "text-base" : ""}`}
            />
            <button className={`${isMobile ? "w-full" : ""} px-8 py-4 bg-[#C9F400] hover:bg-[#d4f520] text-black font-bold ${isMobile ? "text-sm" : "text-base"} rounded-xl transition-all shadow-lg shadow-[#C9F400]/20 whitespace-nowrap`}>
              {data.cityPageCtaText || "GET PRIORITY ACCESS"}
            </button>
          </div>
          <p className="text-white/30 text-xs mt-3">Get notified when tickets drop</p>
        </div>
      )}
    </div>
  </div>
);

// Consumer Preview Modal
// ============ CONSUMER PREVIEW SYSTEM (IFRAME-BASED) ============

// Consumer Preview Modal - Uses iframe to show the REAL consumer page
const ConsumerPreviewModal = ({ data, onClose, hasUnsavedChanges, onSaveDraft }) => {
  const [device, setDevice] = useState("desktop");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isRealMobile, setIsRealMobile] = useState(false);
  const iframeRef = useRef(null);
  
  // Detect if user is on an actual mobile device
  useEffect(() => {
    const checkMobile = () => setIsRealMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Build the actual consumer-facing URL
  const getPreviewUrl = () => {
    const baseUrl = window.location.origin;
    // Use the city slug to load the actual city page
    return `${baseUrl}/city/${data.slug}`;
  };
  
  const handleCopyLink = () => {
    const previewUrl = getPreviewUrl();
    navigator.clipboard.writeText(previewUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleIframeLoad = () => {
    setLoading(false);
  };
  
  // Reset loading state when device changes
  useEffect(() => {
    setLoading(true);
  }, [device]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black"
    >
      {/* Title Bar - Responsive */}
      <div className="sticky top-0 z-20 bg-[#111]/95 backdrop-blur-xl border-b border-white/[0.08] px-3 md:px-6 py-3 md:py-4">
        <div className="flex items-center justify-between gap-2">
          {/* Left: Title & Status */}
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            <div className="hidden md:flex w-10 h-10 rounded-xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/[0.08] items-center justify-center">
              <Eye className="w-5 h-5 text-white/50" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 md:gap-3">
                <h2 className="text-white font-semibold text-sm md:text-lg truncate">{data.cityName || "City"}</h2>
                <StatusBadge status={data.status} size="small" />
              </div>
              <p className="text-white/40 text-[10px] md:text-xs mt-0.5 hidden sm:block">Consumer Preview</p>
            </div>
          </div>
          
          {/* Center: Device Toggle - Hidden on real mobile devices */}
          {!isRealMobile && (
            <div className="flex items-center gap-1 bg-white/[0.04] rounded-xl p-1 border border-white/[0.06]">
              <button
                onClick={() => setDevice("desktop")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                  device === "desktop" 
                    ? "bg-white/[0.1] text-white" 
                    : "text-white/40 hover:text-white/60"
                }`}
              >
                <Monitor className="w-4 h-4" /> Desktop
              </button>
              <button
                onClick={() => setDevice("mobile")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                  device === "mobile" 
                    ? "bg-white/[0.1] text-white" 
                    : "text-white/40 hover:text-white/60"
                }`}
              >
                <Smartphone className="w-4 h-4" /> Mobile
              </button>
            </div>
          )}
          
          {/* Right: Actions */}
          <div className="flex items-center gap-1 md:gap-3">
            <a
              href={getPreviewUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded-xl text-white/70 text-xs font-semibold transition-all"
            >
              <ExternalLink className="w-4 h-4" />
              Open in New Tab
            </a>
            <a
              href={getPreviewUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="md:hidden p-2 hover:bg-white/[0.06] rounded-lg text-white/50"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
            <button
              onClick={handleCopyLink}
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded-xl text-white/70 text-xs font-semibold transition-all"
            >
              {copied ? <Check className="w-4 h-4 text-[#C9F400]" /> : <Link2 className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy Link"}
            </button>
            <button
              onClick={handleCopyLink}
              className="md:hidden p-2 hover:bg-white/[0.06] rounded-lg text-white/50"
            >
              {copied ? <Check className="w-5 h-5 text-[#C9F400]" /> : <Link2 className="w-5 h-5" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 md:p-2.5 hover:bg-white/[0.06] rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-white/50" />
            </button>
          </div>
        </div>
        
        {/* Unsaved Changes Warning */}
        {hasUnsavedChanges && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 md:gap-3 mt-2 md:mt-3 pt-2 md:pt-3 border-t border-white/[0.06]"
          >
            <AlertCircle className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400/80 text-[10px] md:text-xs font-medium">Save first to see changes</span>
            <button
              onClick={onSaveDraft}
              className="px-2 md:px-3 py-1 bg-amber-400/20 hover:bg-amber-400/30 text-amber-400 text-[10px] md:text-xs font-semibold rounded-lg transition-colors"
            >
              Save
            </button>
          </motion.div>
        )}
      </div>
      
      {/* Preview Content with iframe */}
      <div className="h-[calc(100vh-60px)] md:h-[calc(100vh-72px)] overflow-hidden bg-[#1a1a1a] flex items-start justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={isRealMobile ? "real-mobile" : device}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="w-full h-full"
          >
            {/* On real mobile device OR desktop view: show full-width iframe */}
            {(isRealMobile || device === "desktop") ? (
              <div className="w-full h-full relative">
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#0B0B0F] z-10">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 text-[#C9F400] animate-spin" />
                      <span className="text-white/50 text-sm">Loading preview...</span>
                    </div>
                  </div>
                )}
                <iframe
                  ref={iframeRef}
                  src={getPreviewUrl()}
                  onLoad={handleIframeLoad}
                  className="w-full h-full border-0"
                  style={{ minHeight: 'calc(100vh - 72px)' }}
                  title="Consumer Preview"
                />
              </div>
            ) : (
              /* Desktop viewing mobile: show phone frame mockup */
              <div className="py-8 flex items-start justify-center">
                <div className="relative mx-auto" style={{ width: 430 }}>
                  {/* Phone Frame (outer shell) */}
                  <div className="absolute -inset-4 bg-[#1c1c1e] rounded-[3.5rem] shadow-2xl border-[6px] border-[#2a2a2c]" />
                  
                  {/* Dynamic Island / Notch */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-28 h-8 bg-black rounded-3xl z-20" />
                  
                  {/* Screen Container */}
                  <div 
                    className="relative bg-black rounded-[2.8rem] overflow-hidden"
                    style={{ width: 390, height: 844 }}
                  >
                    {loading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#0B0B0F] z-10">
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="w-6 h-6 text-[#C9F400] animate-spin" />
                          <span className="text-white/50 text-xs">Loading...</span>
                        </div>
                      </div>
                    )}
                    <iframe
                      ref={iframeRef}
                      src={getPreviewUrl()}
                      onLoad={handleIframeLoad}
                      className="w-full h-full border-0 bg-[#0B0B0F]"
                      style={{ width: 390, height: 844 }}
                      title="Consumer Preview - Mobile"
                    />
                  </div>
                  
                  {/* Home Indicator */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-36 h-1.5 bg-white/40 rounded-full z-20" />
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ============ PASSWORD GATE ============
const PasswordGate = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/verify?password=${encodeURIComponent(password)}`, { method: "POST" });
      if (res.ok) { localStorage.setItem("adminAuth", "true"); onSuccess(); }
      else setError("Invalid access code");
    } catch { setError("Connection error"); }
    finally { setLoading(false); }
  };
  
  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="text-center mb-12">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/[0.08] flex items-center justify-center shadow-2xl">
            <Lock className="w-7 h-7 text-white/40" />
          </div>
          <h1 className="text-white text-2xl font-semibold tracking-tight">Event Control</h1>
          <p className="text-white/40 text-sm mt-2">Cinco De Crawl Admin</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-2xl px-5 py-4 text-white text-center text-lg tracking-[0.3em] placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/40 transition-all" autoFocus />
          {error && <p className="text-red-400 text-xs text-center">{error}</p>}
          <button type="submit" disabled={loading || !password}
            className="w-full bg-[#C9F400] hover:bg-[#d4f520] disabled:opacity-30 text-black py-4 rounded-2xl font-semibold text-sm uppercase tracking-wider transition-all shadow-lg shadow-[#C9F400]/20">
            {loading ? "Verifying..." : "Access Dashboard"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// ============ CITY EDITOR ============
const CityEditor = ({ city, onSave, onCancel, onDuplicate, showToast, isMobile = false }) => {
  const [formData, setFormData] = useState({
    cityName: "", state: "", slug: "", eventDate: "May 5, 2026", status: "draft",
    ticketUrl: "", vipUrl: "", heroVideoUrl: "", heroImageUrl: "", heroBackgroundStyle: "skull",
    shortLocationLine: "", mapPinLat: "", mapPinLng: "", mapTitle: "", districtDescription: "", order: 0,
    whatYouGet: [], timeline: [], featuredVenues: [], neighborhoodDescription: "",
    hotelsNearby: [], ridePlanCopy: "", faq: [], moreVenuesComing: false, syncCountdown: true,
    comingSoonHeadline: "ARE YOU READY?", comingSoonSubline: "Tickets dropping soon.",
    emailCaptureEnabled: true, cityPageCtaText: "GET PRIORITY ACCESS",
  });
  
  const [previewMode, setPreviewMode] = useState("card");
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const initialDataRef = useRef(null);
  
  useEffect(() => {
    if (city) {
      const data = { ...formData, ...city, 
        mapPinLat: city.mapPinLat || "", mapPinLng: city.mapPinLng || "",
        whatYouGet: city.whatYouGet || [], timeline: city.timeline || [], 
        featuredVenues: city.featuredVenues || [], hotelsNearby: city.hotelsNearby || [], faq: city.faq || [],
        status: city.status === "on_sale" ? "live" : city.status || "draft" 
      };
      setFormData(data);
      initialDataRef.current = JSON.stringify(data);
    } else {
      initialDataRef.current = JSON.stringify(formData);
    }
    setHasChanges(false);
  }, [city]);
  
  const handleChange = useCallback((field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if ((field === "cityName" || field === "state") && !city) {
        const cn = field === "cityName" ? value : prev.cityName;
        const st = field === "state" ? value : prev.state;
        if (cn && st) updated.slug = `${cn.toLowerCase().replace(/\s+/g, '-')}-${st.toLowerCase()}`;
      }
      if (field === "cityName" && !prev.shortLocationLine) updated.shortLocationLine = `Downtown ${value}`;
      setHasChanges(JSON.stringify(updated) !== initialDataRef.current);
      return updated;
    });
  }, [city]);
  
  const handleSave = async (publish = false) => {
    setSaving(true);
    try {
      const payload = { ...formData,
        status: publish ? "on_sale" : (formData.status === "live" ? "on_sale" : formData.status),
        mapPinLat: formData.mapPinLat ? parseFloat(formData.mapPinLat) : null,
        mapPinLng: formData.mapPinLng ? parseFloat(formData.mapPinLng) : null,
        order: parseInt(formData.order) || 0,
      };
      if (payload.status === "live") payload.status = "on_sale";
      
      const url = city ? `${API_URL}/api/cities/${city.slug}` : `${API_URL}/api/cities`;
      const res = await fetch(url, { method: city ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      
      if (res.ok) {
        const saved = await res.json();
        setHasChanges(false);
        initialDataRef.current = JSON.stringify(formData);
        showToast(publish ? "City published!" : "Draft saved", "success");
        onSave(saved);
      } else {
        const err = await res.json();
        showToast(err.detail || "Save failed", "error");
      }
    } catch { showToast("Connection error", "error"); }
    finally { setSaving(false); }
  };
  
  const handleRevert = () => {
    if (initialDataRef.current) {
      setFormData(JSON.parse(initialDataRef.current));
      setHasChanges(false);
      showToast("Changes reverted", "success");
    }
  };
  
  const previewOptions = [
    { id: "card", label: "City Grid Card", icon: LayoutGrid },
    { id: "live", label: "Live City Page", icon: Globe },
    { id: "coming", label: "Coming Soon Page", icon: Clock },
  ];

  return (
    <div className="h-full flex flex-col bg-[#0c0c0e]">
      {/* Top Toolbar */}
      <div className="flex-shrink-0 bg-[#0f0f11] border-b border-white/[0.06] px-4 md:px-6 py-3 md:py-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            {/* Back button for mobile */}
            {isMobile && (
              <button onClick={onCancel} className="p-2 -ml-2 hover:bg-white/[0.06] rounded-xl text-white/50">
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
            )}
            <h2 className="text-white font-semibold text-base md:text-xl tracking-tight truncate">{formData.cityName || "New City"}</h2>
            <StatusBadge status={formData.status} size={isMobile ? "small" : "default"} glow />
            {hasChanges && !isMobile && (
              <motion.span 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-1.5 text-amber-400/80 text-[10px] uppercase tracking-wider font-semibold"
              >
                <motion.div className="w-1.5 h-1.5 rounded-full bg-amber-400" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                Unsaved
              </motion.span>
            )}
          </div>
          
          {/* Desktop Actions */}
          {!isMobile && (
            <div className="flex items-center gap-2">
              <button onClick={handleRevert} disabled={!hasChanges}
                className="px-4 py-2 text-white/40 hover:text-white/70 text-xs uppercase tracking-wider font-semibold transition-colors disabled:opacity-30 flex items-center gap-1.5 hover:bg-white/[0.04] rounded-lg">
                <RotateCcw className="w-3.5 h-3.5" /> Revert
              </button>
              {city && (
                <button onClick={() => onDuplicate(formData)}
                  className="px-4 py-2 text-white/40 hover:text-white/70 text-xs uppercase tracking-wider font-semibold transition-colors flex items-center gap-1.5 hover:bg-white/[0.04] rounded-lg">
                  <Copy className="w-3.5 h-3.5" /> Duplicate
                </button>
              )}
              <button onClick={() => handleSave(false)} disabled={saving}
                className="px-5 py-2 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] rounded-xl text-white/80 text-xs uppercase tracking-wider font-semibold transition-all flex items-center gap-2">
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save Draft"}
              </button>
              <button onClick={() => handleSave(true)} disabled={saving}
                className="px-5 py-2 bg-[#C9F400] hover:bg-[#d4f520] rounded-xl text-black text-xs uppercase tracking-wider font-bold transition-all flex items-center gap-2 shadow-lg shadow-[#C9F400]/20">
                <Globe className="w-3.5 h-3.5" /> Publish
              </button>
              <button onClick={() => setShowFullPreview(true)}
                className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-xl text-white/70 hover:text-white transition-all flex items-center gap-2">
                <Eye className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-wider">Preview</span>
              </button>
              <button onClick={onCancel} className="p-2 hover:bg-white/[0.06] rounded-xl text-white/30 hover:text-white/60 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Mobile Actions */}
          {isMobile && (
            <div className="flex items-center gap-1">
              <button onClick={() => setShowFullPreview(true)} className="p-2 hover:bg-white/[0.06] rounded-lg text-white/50">
                <Eye className="w-5 h-5" />
              </button>
              <button onClick={() => handleSave(false)} disabled={saving} className="p-2 hover:bg-white/[0.06] rounded-lg text-white/50">
                <Save className="w-5 h-5" />
              </button>
              <button onClick={() => handleSave(true)} disabled={saving}
                className="px-3 py-1.5 bg-[#C9F400] rounded-lg text-black text-xs font-bold">
                {saving ? "..." : "Publish"}
              </button>
            </div>
          )}
        </div>
        
        {/* Status Row */}
        <div className={`flex items-center gap-3 md:gap-6 mt-3 md:mt-4 pt-3 md:pt-4 border-t border-white/[0.04] ${isMobile ? 'overflow-x-auto pb-1' : ''}`}>
          <span className="text-[10px] text-white/30 uppercase tracking-wider font-semibold flex-shrink-0">Status</span>
          <div className="flex gap-1.5">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <button key={key} onClick={() => handleChange("status", key)}
                className={`px-3 md:px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                  formData.status === key ? "text-black shadow-lg" : "text-white/40 hover:text-white/60 hover:bg-white/[0.04]"
                }`}
                style={formData.status === key ? { background: cfg.color, boxShadow: `0 4px 12px ${cfg.color}40` } : {}}>
                {cfg.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Mobile unsaved indicator */}
        {isMobile && hasChanges && (
          <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t border-white/[0.04]">
            <motion.div className="w-1.5 h-1.5 rounded-full bg-amber-400" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
            <span className="text-amber-400/80 text-[10px] uppercase tracking-wider font-semibold">Unsaved changes</span>
          </div>
        )}
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Form Panel */}
        <div className={`flex-1 overflow-y-auto ${isMobile ? 'p-4' : 'p-6'}`}>
          <div className="max-w-2xl mx-auto space-y-1">
            {/* Basic Information */}
            <Section title="Basic Information" icon={FileText} defaultOpen={true}>
              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <Input label="City Name" value={formData.cityName} onChange={(e) => handleChange("cityName", e.target.value)} placeholder="Houston" required />
                <Input label="State" value={formData.state} onChange={(e) => handleChange("state", e.target.value.toUpperCase())} placeholder="TX" maxLength={2} required />
              </div>
              <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-3'} gap-4`}>
                <Input label="URL Slug" value={formData.slug} onChange={(e) => handleChange("slug", e.target.value.toLowerCase().replace(/\s+/g, '-'))} placeholder="houston-tx" className={isMobile ? '' : 'col-span-2'} />
                <Input label="Order" type="number" value={formData.order} onChange={(e) => handleChange("order", e.target.value)} />
              </div>
              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <Input label="Event Date" value={formData.eventDate} onChange={(e) => handleChange("eventDate", e.target.value)} placeholder="May 5, 2026" />
                <Input label="Location Line" value={formData.shortLocationLine} onChange={(e) => handleChange("shortLocationLine", e.target.value)} placeholder="Downtown Houston" />
              </div>
              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <Input label="Ticket URL" value={formData.ticketUrl} onChange={(e) => handleChange("ticketUrl", e.target.value)} placeholder="https://..." />
                <Input label="VIP URL" value={formData.vipUrl} onChange={(e) => handleChange("vipUrl", e.target.value)} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Hero Background</label>
                <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-4'} gap-2`}>
                  {HERO_STYLES.map((style) => (
                    <button key={style.id} onClick={() => handleChange("heroBackgroundStyle", style.id)}
                      className={`py-3 rounded-xl text-xs font-semibold transition-all flex flex-col items-center gap-1.5 ${
                        formData.heroBackgroundStyle === style.id 
                          ? "bg-white/[0.1] text-white border border-white/20 shadow-lg" 
                          : "bg-white/[0.02] text-white/40 border border-transparent hover:bg-white/[0.05]"
                      }`}>
                      <span className="text-lg">{style.icon}</span>
                      <span className="uppercase tracking-wider">{style.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {formData.heroBackgroundStyle === "video" && (
                <FileUploader label="Hero Video" value={formData.heroVideoUrl} onChange={(url) => handleChange("heroVideoUrl", url)} />
              )}
              {formData.heroBackgroundStyle === "image" && (
                <FileUploader label="Hero Image" value={formData.heroImageUrl} onChange={(url) => handleChange("heroImageUrl", url)} accept="image/*" />
              )}
            </Section>
            
            {/* Countdown System */}
            <Section title="Countdown System" icon={Clock}>
              <Toggle 
                label="Sync countdown across all cities" 
                description="All cities will count down to May 5, 2026"
                value={formData.syncCountdown} 
                onChange={(val) => handleChange("syncCountdown", val)} 
              />
            </Section>
            
            {/* Map Configuration */}
            <Section title="Map Configuration" icon={Map}>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Latitude" type="number" step="0.0001" value={formData.mapPinLat} onChange={(e) => handleChange("mapPinLat", e.target.value)} placeholder="29.7604" />
                <Input label="Longitude" type="number" step="0.0001" value={formData.mapPinLng} onChange={(e) => handleChange("mapPinLng", e.target.value)} placeholder="-95.3698" />
              </div>
              <Input label="Custom Map Title" value={formData.mapTitle} onChange={(e) => handleChange("mapTitle", e.target.value)} placeholder="The Crawl Zone" />
              <Textarea label="District Description" value={formData.districtDescription} onChange={(e) => handleChange("districtDescription", e.target.value)} placeholder="All venues within walking distance..." />
            </Section>
            
            {/* Venues */}
            <Section title="Venues" icon={Building} badge={`${formData.featuredVenues?.length || 0}`}>
              <VenueBuilder venues={formData.featuredVenues} setVenues={(v) => handleChange("featuredVenues", v)} />
              <Toggle 
                label="More venues coming" 
                description="Show 'more venues TBA' on the page"
                value={formData.moreVenuesComing} 
                onChange={(val) => handleChange("moreVenuesComing", val)} 
              />
            </Section>
            
            {/* What You Get */}
            <Section title="What You Get" icon={Sparkles} badge={`${formData.whatYouGet?.length || 0}`}>
              <FeatureBuilder features={formData.whatYouGet} setFeatures={(f) => handleChange("whatYouGet", f)} />
            </Section>
            
            {/* Hotels */}
            <Section title="Hotels" icon={Hotel} badge={`${formData.hotelsNearby?.length || 0}`}>
              <HotelBuilder hotels={formData.hotelsNearby} setHotels={(h) => handleChange("hotelsNearby", h)} />
            </Section>
            
            {/* FAQ */}
            <Section title="FAQ" icon={HelpCircle} badge={`${formData.faq?.length || 0}`}>
              <FAQBuilder faq={formData.faq} setFaq={(f) => handleChange("faq", f)} />
            </Section>
            
            {/* Coming Soon Settings */}
            {(formData.status === "coming_soon" || formData.status === "draft") && (
              <Section title="Coming Soon Settings" icon={Clock}>
                <Input label="Headline" value={formData.comingSoonHeadline} onChange={(e) => handleChange("comingSoonHeadline", e.target.value)} placeholder="ARE YOU READY?" />
                <Input label="Subline" value={formData.comingSoonSubline} onChange={(e) => handleChange("comingSoonSubline", e.target.value)} placeholder="Tickets dropping soon." />
                <Input label="CTA Button Text" value={formData.cityPageCtaText} onChange={(e) => handleChange("cityPageCtaText", e.target.value)} placeholder="GET PRIORITY ACCESS" />
                <Toggle label="Email Capture" description="Show email signup form" value={formData.emailCaptureEnabled} onChange={(val) => handleChange("emailCaptureEnabled", val)} />
              </Section>
            )}
          </div>
        </div>
        
        {/* Preview Panel - Hidden on mobile */}
        {!isMobile && (
          <div className="w-[420px] flex-shrink-0 border-l border-white/[0.06] bg-[#0a0a0c] flex flex-col">
            <div className="flex-shrink-0 p-5 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye className="w-4 h-4 text-white/30" />
                <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Live Preview</span>
              </div>
              <div className="relative">
                <select value={previewMode} onChange={(e) => setPreviewMode(e.target.value)}
                  className="appearance-none bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2 pr-9 text-white/70 text-xs font-medium focus:outline-none focus:border-[#C9F400]/30 cursor-pointer">
                  {previewOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="m-4 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/[0.06] overflow-hidden backdrop-blur-sm">
                {previewMode === "card" && <PreviewCityCard data={formData} />}
                {previewMode === "live" && <PreviewLivePage data={formData} />}
                {previewMode === "coming" && <PreviewComingSoon data={formData} />}
              </div>
            </div>
          </div>
        )}
      </div>
      
      <AnimatePresence>
        {showFullPreview && (
          <ConsumerPreviewModal 
            data={formData} 
            onClose={() => setShowFullPreview(false)} 
            hasUnsavedChanges={hasChanges}
            onSaveDraft={() => handleSave(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ============ MAIN ADMIN PAGE ============
const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("order");
  const [filterStatus, setFilterStatus] = useState("all");
  const [toast, setToast] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileEditor, setShowMobileEditor] = useState(false);
  
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };
  
  useEffect(() => {
    if (localStorage.getItem("adminAuth") === "true") setAuthenticated(true);
  }, []);
  
  useEffect(() => {
    if (authenticated) fetchCities();
  }, [authenticated]);
  
  const fetchCities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cities`);
      if (res.ok) {
        const data = await res.json();
        setCities(data.map(c => ({ ...c, status: c.status === "on_sale" ? "live" : c.status })));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  
  const handleSave = (saved) => {
    const mapped = { ...saved, status: saved.status === "on_sale" ? "live" : saved.status };
    setCities(prev => {
      const exists = prev.find(c => c.id === mapped.id);
      if (exists) return prev.map(c => c.id === mapped.id ? mapped : c);
      return [...prev, mapped];
    });
    setSelectedCity(null);
    setIsCreating(false);
    setShowMobileEditor(false);
  };
  
  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setIsCreating(false);
    if (isMobile) setShowMobileEditor(true);
  };
  
  const handleCreateNew = () => {
    setIsCreating(true);
    setSelectedCity(null);
    if (isMobile) setShowMobileEditor(true);
  };
  
  const handleCloseEditor = () => {
    setSelectedCity(null);
    setIsCreating(false);
    setShowMobileEditor(false);
  };
  
  const handleDuplicate = (cityData) => {
    const duplicated = {
      ...cityData,
      id: undefined,
      slug: `${cityData.slug}-copy`,
      cityName: `${cityData.cityName} (Copy)`,
      status: "draft",
    };
    setSelectedCity(null);
    setIsCreating(true);
    setTimeout(() => setSelectedCity(duplicated), 100);
  };
  
  const filteredCities = useMemo(() => {
    return cities
      .filter(city => {
        const matchesSearch = city.cityName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              city.state?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = filterStatus === "all" || city.status === filterStatus;
        return matchesSearch && matchesFilter;
      })
      .sort((a, b) => {
        if (sortBy === "name") return (a.cityName || "").localeCompare(b.cityName || "");
        if (sortBy === "status") return (a.status || "").localeCompare(b.status || "");
        return (a.order || 0) - (b.order || 0);
      });
  }, [cities, searchQuery, filterStatus, sortBy]);
  
  if (!authenticated) return <PasswordGate onSuccess={() => setAuthenticated(true)} />;
  
  return (
    <div className="h-screen bg-[#09090b] flex overflow-hidden">
      {/* Left Sidebar - Full width on mobile */}
      <div className={`${isMobile ? 'w-full' : 'w-[360px]'} flex-shrink-0 border-r border-white/[0.06] flex flex-col bg-[#0c0c0e]`}>
        {/* Header */}
        <div className="p-5 border-b border-white/[0.06]">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-white font-semibold text-lg tracking-tight">Event Control</h1>
              <p className="text-white/30 text-xs mt-0.5">Cinco De Crawl</p>
            </div>
            <button onClick={() => { localStorage.removeItem("adminAuth"); setAuthenticated(false); }}
              className="p-2 hover:bg-white/[0.04] rounded-lg transition-colors">
              <Lock className="w-4 h-4 text-white/30" />
            </button>
          </div>
          
          {/* Add City Button */}
          <button onClick={handleCreateNew}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#C9F400]/15 to-[#C9F400]/5 hover:from-[#C9F400]/25 hover:to-[#C9F400]/10 border border-[#C9F400]/20 hover:border-[#C9F400]/40 text-[#C9F400] transition-all duration-300 flex items-center justify-center gap-2.5 group shadow-lg shadow-[#C9F400]/5">
            <Plus className="w-4 h-4 group-hover:scale-110 transition-transform" />
            <span className="text-xs font-bold uppercase tracking-wider">Add New City</span>
          </button>
        </div>
        
        {/* Search & Filters */}
        <div className="p-4 space-y-3 border-b border-white/[0.04]">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
            <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search cities..."
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl pl-11 pr-4 py-3 text-white/90 text-sm placeholder:text-white/20 focus:outline-none focus:border-[#C9F400]/30 transition-all" />
          </div>
          
          {/* Filters */}
          <div className="flex gap-1.5">
            {["all", "live", "coming_soon", "draft"].map((f) => (
              <button key={f} onClick={() => setFilterStatus(f)}
                className={`flex-1 py-2 rounded-lg text-[9px] font-bold uppercase tracking-wider transition-all ${
                  filterStatus === f 
                    ? "bg-white/[0.1] text-white" 
                    : "text-white/30 hover:text-white/50 hover:bg-white/[0.04]"
                }`}>
                {f === "all" ? "All" : f === "coming_soon" ? "Soon" : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
        
        {/* Sort & Count */}
        <div className="px-5 py-3 flex items-center justify-between">
          <span className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">{filteredCities.length} cities</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent text-[10px] text-white/40 uppercase tracking-wider font-semibold focus:outline-none cursor-pointer">
            <option value="order">Display Order</option>
            <option value="name">A–Z</option>
            <option value="status">Status</option>
          </select>
        </div>
        
        {/* City List */}
        <div className="flex-1 overflow-y-auto px-3 pb-3">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredCities.map((city) => {
                const isActive = selectedCity?.id === city.id;
                const cfg = STATUS_CONFIG[city.status] || STATUS_CONFIG.draft;
                
                return (
                  <motion.button 
                    key={city.id} 
                    onClick={() => handleCitySelect(city)}
                    className={`w-full text-left p-4 rounded-2xl transition-all duration-200 relative group ${
                      isActive && !isMobile ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                    }`}
                    style={isActive && !isMobile ? { boxShadow: cfg.glow } : {}}
                    whileTap={{ scale: 0.99 }}
                  >
                    {isActive && !isMobile && (
                      <motion.div 
                        layoutId="activeCityBar" 
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 rounded-full"
                        style={{ background: cfg.color }}
                      />
                    )}
                    
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab hidden md:block">
                          <GripVertical className="w-4 h-4 text-white/20" />
                        </div>
                        <div>
                          <h3 className={`font-semibold text-sm ${isActive && !isMobile ? "text-white" : "text-white/70"}`}>
                            {city.cityName}, {city.state}
                          </h3>
                          <p className="text-white/30 text-xs mt-1 flex items-center gap-1.5">
                            <MapPin className="w-3 h-3" /> 
                            {city.shortLocationLine || `Downtown ${city.cityName}`}
                          </p>
                        </div>
                      </div>
                      <StatusBadge status={city.status} size="small" glow={isActive && !isMobile} />
                    </div>
                    
                    {/* Hover Actions - Desktop only */}
                    <div className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex gap-1">
                      <div 
                        role="button" 
                        tabIndex={0}
                        className="p-1.5 hover:bg-white/[0.1] rounded-lg transition-colors cursor-pointer" 
                        onClick={(e) => { e.stopPropagation(); handleDuplicate(city); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleDuplicate(city); }}}
                      >
                        <Copy className="w-3.5 h-3.5 text-white/40" />
                      </div>
                    </div>
                    
                    {/* Mobile chevron */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 md:hidden">
                      <ChevronRight className="w-4 h-4 text-white/20" />
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Right Panel - Hidden on mobile */}
      {!isMobile && (
        <div className="flex-1 min-w-0">
          {isCreating || selectedCity ? (
            <CityEditor
              city={isCreating ? null : selectedCity}
              onSave={handleSave}
              onCancel={handleCloseEditor}
              onDuplicate={handleDuplicate}
              showToast={showToast}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 bg-[#0a0a0c]">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/[0.08] flex items-center justify-center mb-6 shadow-2xl">
                <MapPin className="w-9 h-9 text-white/20" />
              </div>
              <h3 className="text-white/70 font-semibold text-xl">Select a city to edit</h3>
              <p className="text-white/30 text-sm mt-2 max-w-xs">Choose a city from the sidebar or create a new one to get started with the event builder.</p>
            </div>
          )}
        </div>
      )}
      
      {/* Mobile Editor Modal */}
      <AnimatePresence>
        {isMobile && showMobileEditor && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-0 z-[100] bg-[#0c0c0e]"
          >
            <CityEditor
              city={isCreating ? null : selectedCity}
              onSave={handleSave}
              onCancel={handleCloseEditor}
              onDuplicate={handleDuplicate}
              showToast={showToast}
              isMobile={true}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
